import {
  Sprout, Hourglass, Moon, Droplets, Atom, Salad, HeartPulse, Baby,
} from 'lucide-react';

// Shared wellness pillar data — used by the Landing page grid and the
// individual pillar deep-dive pages (/pillars/:slug).
export const pillars = [
  {
    slug: 'fertility',
    title: 'Fertility',
    icon: Sprout,
    image: 'https://images.unsplash.com/photo-1518708909080-704599b19972?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'Preparing your body',
    shortDesc: 'Prepare your body and biology for conception with personalized, preventative nutrition.',
    headline: 'Fertility, supported from the inside out',
    tagline: 'Understand the biology behind conception and give your body a nourishing, personalized foundation — grounded in prevention, not pressure.',
    overview: [
      'Fertility is deeply personal, and it is shaped by far more than age. Your microbiome, nutrition, stress, sleep and environment all influence the terrain your body prepares for conception.',
      'Rejuven translates your genotype and microbiome signals into gentle, preventative guidance you can actually live by — helping you feel informed and in control of the factors within your reach.',
    ],
    helps: [
      { title: 'Nourish your terrain', desc: 'Personalized, whole-food nutrition mapped to your biology and cycle — not generic fertility rules.' },
      { title: 'Balance the everyday', desc: 'Simple lifestyle levers across sleep, stress and movement that support hormonal rhythm.' },
      { title: 'Understand your signals', desc: 'Learn what your microbiome and genotype tendencies may mean for your preconception season.' },
    ],
    focusAreas: ['Preconception nutrition', 'Cycle awareness', 'Stress & sleep rhythm', 'Micronutrient foundations', 'Gut & microbiome health'],
  },
  {
    slug: 'longevity',
    title: 'Longevity',
    icon: Hourglass,
    image: 'https://images.unsplash.com/photo-1567013514336-6de53c9e7e63?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'Ageing on your terms',
    shortDesc: 'Support healthy aging and cellular vitality through microbiome-informed daily habits.',
    headline: 'Longevity that feels like living well',
    tagline: 'Healthy ageing is not about doing everything — it is about doing the right things for your biology, consistently, starting today.',
    overview: [
      'Longevity is the sum of small, repeated choices. The way you eat, move, sleep and recover shapes how your cells age and how vibrant you feel across the decades.',
      'Rejuven helps you focus on the habits that matter most for your unique biology, turning the science of healthy ageing into a calm, sustainable daily rhythm.',
    ],
    helps: [
      { title: 'Protect your energy', desc: 'Nutrition and lifestyle guidance that supports steady energy, metabolic health and recovery.' },
      { title: 'Build resilient habits', desc: 'Sustainable daily rhythms for movement, sleep and stress that compound over time.' },
      { title: 'Personalize the plan', desc: 'Guidance tuned to your microbiome and genotype tendencies — never one-size-fits-all.' },
    ],
    focusAreas: ['Metabolic health', 'Sleep & recovery', 'Movement & strength', 'Antioxidant nutrition', 'Stress resilience'],
  },
  {
    slug: 'menopause',
    title: 'Menopause',
    icon: Moon,
    image: 'https://images.unsplash.com/photo-1717333943146-6b377d81b88f?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'A new season',
    shortDesc: 'Navigate perimenopause and beyond with guidance tuned to your changing biology.',
    headline: 'Menopause, met with understanding',
    tagline: 'Perimenopause and menopause bring real change — and real opportunity. Personalized, preventative guidance helps you feel steady through the shift.',
    overview: [
      'As hormones shift, so does the way your body responds to food, movement, sleep and stress. What worked before may not work now — and that is completely normal.',
      'Rejuven offers lifestyle-first guidance tuned to your biology and stage of life, helping you support energy, sleep, mood and metabolic health through this transition.',
    ],
    helps: [
      { title: 'Steady your energy', desc: 'Nutrition and daily rhythms that support metabolism, mood and steady energy through change.' },
      { title: 'Sleep & stress support', desc: 'Preventative, lifestyle-first strategies for the sleep and stress shifts of this season.' },
      { title: 'Feel informed', desc: 'Clear, unbiased education on what is happening in your body — and the levers within your reach.' },
    ],
    focusAreas: ['Perimenopause changes', 'Sleep & mood', 'Bone & metabolic health', 'Nutrition adjustments', 'Movement for this stage'],
  },
  {
    slug: 'bioidentical-hormones',
    title: 'Bioidentical Hormones',
    icon: Droplets,
    image: 'https://images.unsplash.com/photo-1461468611824-46457c0e11fd?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'Understanding hormones',
    shortDesc: 'Understand hormone balance and lifestyle levers that support how you feel.',
    headline: 'Hormone education, without the confusion',
    tagline: 'Understand what bioidentical hormones are, how they are discussed, and the lifestyle factors that shape how you feel — clearly and honestly.',
    overview: [
      'Hormones influence almost everything — energy, mood, sleep, skin and metabolism. The conversation around bioidentical hormones can be overwhelming and full of conflicting claims.',
      'Rejuven provides clear, balanced education to help you understand the topic and the daily habits that support hormonal wellbeing — so you can have more informed conversations with your own healthcare provider.',
    ],
    helps: [
      { title: 'Learn the language', desc: 'Plain-English education on hormones and how bioidentical hormones are discussed.' },
      { title: 'Support naturally', desc: 'Nutrition, sleep and lifestyle levers that support how you feel day to day.' },
      { title: 'Ask better questions', desc: 'Feel prepared for informed conversations with your own licensed healthcare provider.' },
    ],
    focusAreas: ['Hormone basics', 'Lifestyle support', 'Nutrition & balance', 'Symptom awareness', 'Informed conversations'],
    note: 'Rejuven provides educational information only. We do not prescribe, dose or manage hormones. Any hormone therapy decisions should be made with a licensed healthcare provider.',
  },
  {
    slug: 'peptide-insights',
    title: 'Peptide Insights',
    icon: Atom,
    image: 'https://images.unsplash.com/photo-1625161470828-05a772c3cde5?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'Curiosity, informed',
    shortDesc: 'Explore peptides and how they may interact with your unique biology — clearly explained.',
    headline: 'Peptides, explained clearly and honestly',
    tagline: 'Peptides are everywhere in wellness conversations. We help you understand what they are and how they may interact with biology — for understanding, never prescription.',
    overview: [
      'Peptides have become a popular topic in longevity and wellness circles, but reliable, unbiased information is hard to find amid the hype.',
      'Rejuven offers educational insight into peptides and how they may interact with your biology, so you can separate signal from noise and bring thoughtful questions to a qualified professional.',
    ],
    helps: [
      { title: 'Cut through the hype', desc: 'Balanced, educational explanations of peptides and the claims surrounding them.' },
      { title: 'Understand interactions', desc: 'Learn how peptides are discussed in relation to your biology and wellness goals.' },
      { title: 'Stay grounded', desc: 'Guidance that always points back to your own licensed healthcare provider for decisions.' },
    ],
    focusAreas: ['What peptides are', 'Common categories', 'Biology interactions', 'Evaluating claims', 'Safety-first mindset'],
    note: 'Rejuven provides educational information about peptides only. We do not sell, prescribe, dose or recommend peptide protocols. Always consult a licensed healthcare provider before considering any peptide.',
  },
  {
    slug: 'general-nutrition',
    title: 'General Nutrition',
    icon: Salad,
    image: 'https://images.unsplash.com/photo-1494390248081-4e521a5940db?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'Food that fits you',
    shortDesc: 'Everyday nutrition mapped to your genotype and gut, not one-size-fits-all rules.',
    headline: 'Nutrition personalized to your biology',
    tagline: 'There is no single perfect diet — only the one that fits your body, your gut and your life. We help you find yours.',
    overview: [
      'Generic diet advice ignores the very thing that makes nutrition powerful: you. Your microbiome and genotype shape how you respond to different foods.',
      'Rejuven turns those signals into everyday, whole-food guidance that is realistic, flexible and built around how you actually live.',
    ],
    helps: [
      { title: 'Eat for your gut', desc: 'Whole-food guidance that supports a diverse, resilient microbiome.' },
      { title: 'Personalized, not rigid', desc: 'Flexible nutrition mapped to your biology — no punishing rules or fads.' },
      { title: 'Sustainable habits', desc: 'Small, repeatable changes that fit your life and last beyond a trend.' },
    ],
    focusAreas: ['Microbiome-friendly foods', 'Whole-food foundations', 'Blood-sugar balance', 'Micronutrients', 'Sustainable habits'],
  },
  {
    slug: 'early-adult-wellness',
    title: 'Early-Adult Wellness',
    icon: HeartPulse,
    image: 'https://images.unsplash.com/photo-1612994451093-c6791c8989cd?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'Foundations for life',
    shortDesc: 'Build resilient foundations for energy, skin, cycles and mood in your 20s and 30s.',
    headline: 'Build strong foundations early',
    tagline: 'The habits you build in your 20s and 30s shape how you feel for decades. Start with a foundation designed for your biology.',
    overview: [
      'Early adulthood is the ideal time to invest in wellbeing — energy, skin, cycles, mood and metabolism all respond beautifully to the right foundations.',
      'Rejuven helps younger women understand their biology early and build sustainable, preventative habits that pay dividends for a lifetime.',
    ],
    helps: [
      { title: 'Energy & mood', desc: 'Nutrition and lifestyle guidance to support steady energy, focus and mood.' },
      { title: 'Skin & cycles', desc: 'Understand how your biology shows up in your skin and menstrual cycle.' },
      { title: 'Habits that last', desc: 'Simple, sustainable routines that build lifelong resilience.' },
    ],
    focusAreas: ['Cycle health', 'Skin & gut connection', 'Energy & focus', 'Stress foundations', 'Sustainable routines'],
  },
  {
    slug: 'child-nutrition',
    title: 'Child Nutrition',
    icon: Baby,
    image: 'https://images.unsplash.com/photo-1551934262-db2d7dd517f4?w=900&q=85&auto=format&fit=crop',
    eyebrow: 'The next generation',
    shortDesc: 'Nourish the next generation with epigenetics-aware, whole-food guidance.',
    headline: 'Nourishing the next generation',
    tagline: 'The earliest years shape lifelong health. Give your child a strong start with whole-food, epigenetics-aware nutrition guidance.',
    overview: [
      'A child\u2019s developing microbiome and eating habits lay the groundwork for lifelong wellbeing. Small, thoughtful choices early on can have an outsized impact.',
      'Rejuven offers gentle, educational guidance to help families build whole-food foundations and a positive relationship with food from the very beginning.',
    ],
    helps: [
      { title: 'Whole-food first', desc: 'Simple, family-friendly guidance centered on real, nourishing foods.' },
      { title: 'Support the microbiome', desc: 'Understand how early nutrition shapes a child\u2019s developing gut and immunity.' },
      { title: 'Positive food habits', desc: 'Build a calm, healthy relationship with food that lasts into adulthood.' },
    ],
    focusAreas: ['First foods', 'Microbiome development', 'Balanced meals', 'Food relationship', 'Family habits'],
  },
];

export const getPillarBySlug = (slug) => pillars.find((p) => p.slug === slug);
